import { CellAction, CellModel, ColumnSource, RowModel, RowSource } from "@omegagrid/grid";
import { ColorTypes, MenuItem } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ToolbarItem } from "@omegagrid/toolbar";
import { ObjectShare } from "@reporting/rp-types/core";
import rp from "../global";
import { GridEditorAdapter, GridEditorOptions, RowData } from "./gridEditorAdapter";
import { SyncColumn, SyncEditorAdapter, SyncEditorOptions, SyncTypes } from "./syncEditorAdapter";

/**
 * The synchronization and sharing of a module as a grid - the admin view. It shows what `SyncEditor`
 * lists, with the filters and the sorting of the grid on top: rows are deleted by selection (the trash
 * icon, the row menu or Ctrl+-) and the deletions saved at once, the form of the module adds or edits
 * a registration in a modal sidebar, and where the module synchronizes chosen objects, the selected
 * rows can be synchronized alone. The module's `SyncEditorAdapter` stays the source of the data, the
 * form and the actions, so both views share it.
 */
export class SyncGridEditorAdapter extends GridEditorAdapter {

	private syncOptions: SyncEditorOptions;
	private columns: ColumnSource[];

	constructor(readonly sync: SyncEditorAdapter) { super(); }

	private async getSyncOptions() {
		return this.syncOptions ??= this.sync.createOptions(await this.sync.getOptions() ?? {});
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this.columns) return this.columns;
		const options = await this.getSyncOptions();

		const actions: CellAction[] = [
			{ action: 'delete', title: msg('Delete'), icon: 'trash', color: ColorTypes.Red },
			{ action: 'edit', title: msg('Edit'), icon: 'pen', color: ColorTypes.Blue }
		];
		if (options.selectableItems) actions.push({ action: 'syncRow', title: msg('Synchronize'), icon: 'arrows-spin', color: ColorTypes.Green });

		const available: Record<SyncColumn, ColumnSource> = {
			name: { data: 'name', header: msg('Object'), t: 't', w: 220 },
			// a choice column: the filter offers the two types as a selection
			type: { data: 'type', header: msg('Type'), t: 's', w: 130, content: [
				{ k: SyncTypes.SYNC, v: msg('Synchronization') },
				{ k: SyncTypes.SHARE, v: msg('Sharing') },
			] },
			company: { data: 'company', header: msg('Target company'), t: 't', w: 220 },
			name_share: { data: 'name_share', header: msg('Target object'), t: 't', w: 220 },
			date_created: { data: 'date_created', header: msg('Created'), t: 'dt', w: 145 },
			who_created: { data: 'who_created', header: msg('Created by'), t: 't', w: 160 },
		};
		this.columns = [
			{ data: 'controls', t: 'a', e: false, w: 5 + 20 * actions.length, content: actions, filter: false, sorting: false },
			...options.columns.map(column => ({ e: false, ...available[column] })),
			{ data: 'idz', t: 'n', hidden: true, e: false }, // hidden columns go last (select all relies on it)
		];
		return this.columns;
	}

	async getRows(): Promise<RowSource[]> {
		return (await this.sync.getObjects()).map(data => ({ data: {
			...data,
			type: data.only_share ? SyncTypes.SHARE : SyncTypes.SYNC,
			// the target object: empty until the first synchronization, then its name - or a note when the copy
			// was deleted in the target company since (the registration stays and the next synchronization restores it)
			name_share: data.name_share || (!data.only_share && data.id_share_object ? msg('(deleted)') : data.name_share),
			who_created: data.who_created_dv || data.who_created,
		} }));
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		const options = await this.getSyncOptions();
		const toolbar = options.toolbar ?? {};

		// the default texts are resolved when the module loads, before the locale is known: translated here
		const customToolbar: ToolbarItem[] = [];
		if (toolbar.new?.enabled) customToolbar.push({
			type: 'button',
			id: 'add',
			text: msg(toolbar.new.text),
			color: toolbar.new.color,
			icon: toolbar.new.icon,
		});
		if (toolbar.sync?.enabled) customToolbar.push(options.selectableItems ? {
			type: 'dropdown-menu',
			id: 'sync',
			text: msg(toolbar.sync.text),
			color: toolbar.sync.color,
			icon: toolbar.sync.icon,
			width: 140,
			items: [
				{ key: 'sync.all', value: msg('All'), icon: 'arrows-spin' },
				{ key: 'sync.selected', value: msg('Selected rows'), icon: 'list-check' },
			] as MenuItem[] as unknown as ToolbarItem['items'],
		} : {
			type: 'button',
			id: 'sync',
			text: msg(toolbar.sync.text),
			color: toolbar.sync.color,
			icon: toolbar.sync.icon,
		});

		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: 0, // every registration at once: the filters and the sorting work on the client
			editable: false,
			lightTheme: null,
			darkTheme: null,
			refreshAfterSave: true,
			toolbar: {
				refresh: { enabled: toolbar.refresh?.enabled ?? true },
				new: { enabled: false }, // the form adds registrations, not an empty row
			},
			customToolbar,
			rowMenu: { deleteRows: true },
			customMenus: options.selectableItems ? {
				row: { items: [{ key: 'syncRows', value: msg('Synchronize rows'), icon: 'arrows-spin' }], replace: true },
				cell: { items: [], replace: true },
				column: { items: [], replace: true },
			} : undefined,
		});
	}

	/** The save of the grid editor: the rows marked as deleted are removed */
	async save(rowData: RowData[]) {
		for (const row of rowData) {
			const idz = row.data?.idz as number;
			if (row.state === 'deleted' && idz) await this.sync.delete(idz);
		}
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'add': await this.openForm(); break;
			case 'edit': await this.openForm(this.idzOf(cell?.row)); break;
			case 'sync':
			case 'sync.all': await this.synchronize(null); break;
			case 'syncRow': await this.synchronize([this.idzOf(cell?.row)]); break;
			case 'sync.selected': await this.synchronize(this.selectedIds()); break;
			case 'syncRows': await this.synchronize(this.selectedIds(cell)); break;
		}
	}

	private idzOf(row: RowModel): number|undefined {
		return (row?.data as ObjectShare)?.idz;
	}

	/**
	 * The registrations of the selected rows (only those the table shows) - of the clicked row alone
	 * when it lies outside the selection, as the row menu of the grid editor takes it.
	 */
	private selectedIds(cell?: CellModel): number[] {
		const grid = this.editor.grid;
		let { r1, r2 } = grid.selecting.selection.range;
		if (r1 > r2) [r1, r2] = [r2, r1];
		const rows = cell && (cell.row.index < r1 || cell.row.index > r2) ? [cell.row] : grid.model.rows.slice(r1, r2 + 1);
		return rows.filter(row => row.visible).map(row => this.idzOf(row)).filter(idz => idz);
	}

	private async synchronize(ids: number[]|null) {
		if (ids && !ids.filter(idz => idz).length) return;
		this.editor.showLoader();
		try {
			await this.sync.synchronize(ids);
			rp.host.showAlert({ type: 'success', content: msg('Synchronization complete.'), title: msg('Synchronization') });
			await this.refreshIfSaved();
		} catch (e) {
			// the service has shown the error
		} finally {
			this.editor.hideLoader();
		}
	}

	private async openForm(idz: number = null) {
		const component = await this.sync.getEditFormComponent(idz);
		if (!component) return;
		const form = await this.editor.host.openModalSidebar({
			component,
			position: 'left',
			width: 400,
			caption: idz ? msg('Edit') : msg('Add'),
		});
		form.addEventListener('form.save', () => this.refreshIfSaved());
	}

	/** Reloads the rows unless deletions wait to be saved: their save reloads the grid anyway */
	private async refreshIfSaved() {
		if (this.editor.isSaved) await this.editor.refresh();
	}

}
